import { EntryRequest } from 'modules/teams/domain/interfaces/entry-request';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { getBirthDay, getFullName } from 'shared/utils';
import { AsyncState } from 'shared/utils/async-state';

import { theme } from 'styles/theme/theme';
import { UDEntityList, UDEntityRow, UDText } from 'ud-ui/components';

interface WaitingListProps {
  entryRequests: EntryRequest[];
}

/**
 * Список заявок только на чтение.
 *
 * Кнопки «Принять»/«Отклонить» дёргали мутацию `updateEntryRequest`, которой нет
 * ни в одной версии GraphQL-схемы начиная с самой первой, и REST-двойника у неё
 * тоже нет — то есть они всегда завершались ошибкой. Вернуть действия можно,
 * когда бэкенд отдаст ручку смены статуса заявки.
 */
const WaitingList = ({ entryRequests }: WaitingListProps) => {
  const { t } = useTranslation();
  const [filterValue, setFilterValue] = useState('');

  const entryRequestsByFilter = useMemo(() => {
    return entryRequests.filter((entryRequest) => {
      return getFullName(entryRequest.user).toLowerCase().includes(filterValue.toLowerCase());
    });
  }, [entryRequests, filterValue]);

  // Вкладка получает готовый массив и сети не видит: ветки загрузки и ошибки рисует
  // карточка команды выше по дереву (`team-edit-page.tsx`). Считать надо по
  // отфильтрованному списку, иначе ветка «не найдено» никогда не покажется.
  const state: AsyncState = useMemo(
    () => ({ status: entryRequestsByFilter.length > 0 ? 'ready' : 'empty', isRefreshing: false }),
    [entryRequestsByFilter.length],
  );

  const renderEntryRequest = useCallback(
    (entryRequest: EntryRequest, index: number) => (
      <UDEntityRow
        index={index + 1}
        // Фото приходит пустой строкой, а не `null`, поэтому `||`, а не `??`.
        avatar={entryRequest.user.photo?.externalUrl || USER_DEFAULT_AVATAR}
        title={getFullName(entryRequest.user)}
        subtitle={getBirthDay({ birthDate: new Date(entryRequest.user.birthDate ?? '') })}
        actions={
          <UDText type="subhead" color={theme.colors.SurfaceRomanSilver30}>
            {t('entryRequest.status.CREATED')}
          </UDText>
        }
      />
    ),
    [t],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col pb-6">
      <UDEntityList<EntryRequest>
        items={entryRequestsByFilter}
        renderItem={renderEntryRequest}
        getItemKey={(entryRequest) => entryRequest.id}
        state={state}
        search={filterValue}
        onSearchChange={setFilterValue}
        searchPlaceholder={t('entryRequest.waitingList.filter.inputPlaceholder')}
        emptyText={t('entryRequest.waitingList.empty')}
        notFoundText={t('entryRequest.waitingList.notFound')}
      />
    </div>
  );
};

export default WaitingList;
